import dropRight from 'lodash/dropRight';
import get from 'lodash/get';
import last from 'lodash/last';
import set from 'lodash/set';

import { NavigationMapItem, NavigationMap } from '@lumapps/navigation/types';
import createSlice, { PayloadAction } from '@lumapps/redux/createSlice';
import { generateUUID } from '@lumapps/utils/string/generateUUID';
import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';

export interface NavigationAdminState {
    /** The loading state of the navigationMap */
    loadingState: BaseLoadingStatus;
    /** Whether the navigation has been changed. */
    isChanged: boolean;
    /** Whether the navigation is editing. */
    isEditing: boolean;
    /** navigation map node tree. */
    navigationMap?: NavigationMap;
    /**
     * This is a temporal uuid that will change each time there is a change in the nodes themselves. Why?
     * Changing a node or maybe changing the order of the nodes is a deep change, one that Redux does not detect.
     * This means that the UI is not refreshed when there is a change at that level. In order to make the UI refresh,
     * each time there is a change on a node level, we update this id with a new one and allow the UI to refresh itself.
     */
    uuid: string;
}

/**
 * Initial NavigationAdmin state
 */
const initialState: NavigationAdminState = {
    loadingState: BaseLoadingStatus.loading,
    isChanged: false,
    isEditing: false,
    navigationMap: undefined,
    uuid: generateUUID(),
};

export const reducers = {
    /**
     * Set the navigation map
     */
    saveNavigationMap(state: NavigationAdminState, action: PayloadAction<NavigationMap>) {
        set(state, 'loadingState', BaseLoadingStatus.idle);
        set(state, 'navigationMap', action.payload);
        set(state, 'isChanged', false);
    },
    /**
     * Set the navigation map
     */
    setNavigationMap(state: NavigationAdminState, action: PayloadAction<NavigationMap>) {
        set(state, 'navigationMap', action.payload);
    },
    /**
     * Update loading status for the page.
     */
    setLoadingState(state: NavigationAdminState, action: PayloadAction<BaseLoadingStatus>) {
        set(state, 'loadingState', action.payload);
    },
    /**
     * Update editing status for the page.
     */
    setIsEditing(state: NavigationAdminState, action: PayloadAction<boolean>) {
        set(state, 'isEditing', action.payload);
    },
    /**
     * Delete a Node Navigation to the given path.
     */
    deleteNode(state: NavigationAdminState, action: PayloadAction<string[]>) {
        const parentPath = dropRight(action.payload);
        const nodeIndex = last(action.payload);
        const parentNodeList = get(state, ['navigationMap', ...parentPath]);

        // Parent node not found.
        if (!parentNodeList) {
            return;
        }

        // Remove node from parent.
        parentNodeList.splice(nodeIndex, 1);
        set(state, 'isChanged', true);
        set(state, 'uuid', generateUUID());
    },
    /**
     * Add a Node to the current path.
     */
    addNode(state: NavigationAdminState, action: PayloadAction<{ node: NavigationMapItem; path: string[] }>) {
        const { path, node } = action.payload;
        const nodeIndex = get(state, ['navigationMap', ...path])?.length;

        // if nodeIndex is undefined, the current node has no children attribute, create one
        if (nodeIndex === undefined) {
            set(state, ['navigationMap', ...path], [node]);
        } else {
            set(state, ['navigationMap', ...path, nodeIndex], node);
        }
        set(state, 'isChanged', true);
        set(state, 'uuid', generateUUID());
    },
    /** Replaces all nodes on a given path */
    replaceNodesAtPath(
        state: NavigationAdminState,
        action: PayloadAction<{ nodes: NavigationMapItem[]; path: string[] }>,
    ) {
        const { path, nodes } = action.payload;

        set(state, ['navigationMap', ...path], nodes);
        set(state, 'isChanged', true);
        set(state, 'uuid', generateUUID());
    },
    /**
     * Edit the Node at the current path.
     */
    editNode(state: NavigationAdminState, action: PayloadAction<{ node: NavigationMapItem; path: string[] }>) {
        const nodePath = action.payload.path;

        if (nodePath) {
            set(state, ['navigationMap', ...nodePath], action.payload.node);
        }

        set(state, 'isChanged', true);
        set(state, 'uuid', generateUUID());
    },
    setChangedStatus(state: NavigationAdminState, action: PayloadAction<boolean>) {
        set(state, 'isChanged', action.payload);
    },
};

const slice = createSlice({
    domain: 'navigation-admin',
    initialState,
    reducers,
});

const { actions, reducer } = slice;

export { actions, reducer, initialState };
